import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@frontend/components/ui/select"
import { flattenMaskLabels } from "@frontend/pages/case-workspace/radiology/lib/mask-utils"
import type { MaskSchema, ProjectModuleItem } from "@frontend/pages/case-workspace/radiology/types"
import type { MaskAnnotationState, ScoreAnnotationState } from "@shared/lib/history-types"
import { deriveInstanceColor } from "@shared/lib/mask-colormap"
import {
  convertScoreValue,
  createDefaultPiradsScore,
  type DceScore,
  dceReasons,
  dwiReasons,
  getPiradsReasons,
  getScoreOptions,
  type ISUPGrade,
  type Modality,
  type NumericScore,
  type PIRADSScore,
  type PiradsLevel,
  type PiradsZone,
  POTENTIAL_FIELD_MAP,
  REASON_FIELD_MAP,
  SCORE_FIELD_MAP,
  t2Reasons,
  updateZonesList
} from "@shared/lib/pirads-types"
import { ChevronDown } from "lucide-react"
import { useCallback, useMemo, useRef, useState } from "react"

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface PiradsModulePanelProps {
  moduleId: string
  seriesId: string | null
  modules: ProjectModuleItem[]
  masks: MaskAnnotationState[]
  scores: ScoreAnnotationState[]
  onUpsertScore: (score: ScoreAnnotationState) => void
}

// ---------------------------------------------------------------------------
// Helpers — resolve lesion masks from the instance_linked dependency
// ---------------------------------------------------------------------------

interface LesionInfo {
  id: string
  label: string
  color: string
  ordinal: number
}

interface LinkedMaskInfo {
  module: ProjectModuleItem
  sourceLabelNames: string[] | null
}

function findLinkedMaskModule(modules: ProjectModuleItem[], piradsModuleId: string): LinkedMaskInfo | null {
  const piradsModule = modules.find((m) => m.id === piradsModuleId)
  if (!piradsModule) return null

  for (const dep of piradsModule.dependencies) {
    if (dep.dependency_mode === "instance_linked") {
      const target = modules.find((m) => m.id === dep.depends_on_module_id)
      if (target?.template_type === "mask") {
        return {
          module: target,
          sourceLabelNames: dep.source_label_names?.length ? dep.source_label_names : null
        }
      }
    }
  }
  return null
}

function resolveLesions(
  linked: LinkedMaskInfo | null,
  masks: MaskAnnotationState[],
  seriesId: string | null
): LesionInfo[] {
  if (!linked) return []
  const { module: maskModule, sourceLabelNames } = linked

  const schema = (() => {
    try {
      const s = JSON.parse(maskModule.template_schema)
      return s?.type === "mask" ? (s as MaskSchema) : null
    } catch {
      return null
    }
  })()
  if (!schema) return []

  const allLabels = flattenMaskLabels(schema)

  const moduleMasks = masks
    .filter((m) => {
      if (m.projectModuleId !== maskModule.id || m.seriesId !== seriesId) return false
      // If source_label_names is set, only include matching labels
      if (sourceLabelNames && m.labelName && !sourceLabelNames.includes(m.labelName)) return false
      return true
    })
    .sort((a, b) => {
      const av = a.labelValue ?? Number.MAX_SAFE_INTEGER
      const bv = b.labelValue ?? Number.MAX_SAFE_INTEGER
      if (av !== bv) return av - bv
      return a.instanceOrdinal - b.instanceOrdinal
    })

  const labelMap = new Map(allLabels.map((l) => [l.key, l]))

  return moduleMasks.map((mask, idx) => {
    const lbl = labelMap.get(mask.labelName ?? "")
    return {
      id: mask.id,
      label: lbl ? `${lbl.label} ${mask.instanceOrdinal}` : `Lesion ${idx + 1}`,
      color: lbl ? deriveInstanceColor(lbl.color, mask.instanceOrdinal) : "#888",
      ordinal: idx + 1
    }
  })
}

// ---------------------------------------------------------------------------
// Score <-> PIRADSScore mapping (stored in ScoreAnnotationState.values)
// ---------------------------------------------------------------------------

function valuesToPirads(values: Record<string, string | number | boolean | null>): PIRADSScore {
  const base = createDefaultPiradsScore()
  if (values.laterality) base.laterality = values.laterality as PIRADSScore["laterality"]
  if (values.level) base.level = values.level as PIRADSScore["level"]
  if (values.zones) {
    try {
      base.zones =
        typeof values.zones === "string" ? JSON.parse(values.zones) : (values.zones as unknown as PiradsZone[])
    } catch {
      base.zones = []
    }
  }
  if (values.T2Score !== undefined && values.T2Score !== null) base.T2Score = values.T2Score as NumericScore | ""
  if (values.T2Reason !== undefined && values.T2Reason !== null) base.T2Reason = String(values.T2Reason)
  if (values.T2UpDownGrade !== undefined && values.T2UpDownGrade !== null)
    base.T2UpDownGrade = String(values.T2UpDownGrade)
  if (values.DWIScore !== undefined && values.DWIScore !== null) base.DWIScore = values.DWIScore as NumericScore | ""
  if (values.DWIReason !== undefined && values.DWIReason !== null) base.DWIReason = String(values.DWIReason)
  if (values.DWIUpDownGrade !== undefined && values.DWIUpDownGrade !== null)
    base.DWIUpDownGrade = String(values.DWIUpDownGrade)
  if (values.DCEScore !== undefined && values.DCEScore !== null) base.DCEScore = values.DCEScore as DceScore | ""
  if (values.DCEReason !== undefined && values.DCEReason !== null) base.DCEReason = String(values.DCEReason)
  if (values.ISUPGrade !== undefined && values.ISUPGrade !== null) base.ISUPGrade = values.ISUPGrade as ISUPGrade
  return base
}

function piradsToValues(p: PIRADSScore): Record<string, string | number | boolean | null> {
  return {
    laterality: p.laterality,
    level: p.level,
    zones: JSON.stringify(p.zones),
    T2Score: p.T2Score,
    T2Reason: p.T2Reason,
    T2UpDownGrade: p.T2UpDownGrade,
    DWIScore: p.DWIScore,
    DWIReason: p.DWIReason,
    DWIUpDownGrade: p.DWIUpDownGrade,
    DCEScore: p.DCEScore,
    DCEReason: p.DCEReason,
    ISUPGrade: p.ISUPGrade
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PiradsModulePanel({
  moduleId,
  seriesId,
  modules,
  masks,
  scores,
  onUpsertScore
}: PiradsModulePanelProps) {
  const [openLesions, setOpenLesions] = useState<Record<string, boolean>>({})
  const idMapRef = useRef<Map<string, string>>(new Map())

  const linked = useMemo(() => findLinkedMaskModule(modules, moduleId), [modules, moduleId])
  const lesions = useMemo(() => resolveLesions(linked, masks, seriesId), [linked, masks, seriesId])

  // Get or create stable score ID per lesion
  const getScoreId = useCallback(
    (lesionId: string): string => {
      const existing = scores.find(
        (s) => s.projectModuleId === moduleId && s.seriesId === seriesId && s.labelName === lesionId
      )
      if (existing) return existing.id
      if (!idMapRef.current.has(lesionId)) {
        idMapRef.current.set(lesionId, crypto.randomUUID())
      }
      return idMapRef.current.get(lesionId)!
    },
    [moduleId, seriesId, scores]
  )

  const getScoreData = useCallback(
    (lesionId: string): PIRADSScore => {
      const existing = scores.find(
        (s) => s.projectModuleId === moduleId && s.seriesId === seriesId && s.labelName === lesionId
      )
      return existing ? valuesToPirads(existing.values) : createDefaultPiradsScore()
    },
    [moduleId, seriesId, scores]
  )

  const updateField = useCallback(
    (lesionId: string, updates: Partial<PIRADSScore>) => {
      const current = getScoreData(lesionId)
      const merged = { ...current, ...updates }
      onUpsertScore({
        id: getScoreId(lesionId),
        seriesId,
        projectModuleId: moduleId,
        labelName: lesionId,
        labelValue: null,
        values: piradsToValues(merged)
      })
    },
    [getScoreData, getScoreId, moduleId, onUpsertScore, seriesId]
  )

  const toggleLesion = (id: string) => {
    setOpenLesions((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  if (lesions.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-[11px] text-muted-foreground p-4 text-center">
        {linked
          ? "No lesion instances found. Create mask instances in the linked segmentation module first."
          : "No linked mask module found. Add an instance-linked mask dependency to this PI-RADS module in project settings."}
      </div>
    )
  }

  return (
    <div className="flex-1 overflow-auto p-3 space-y-2">
      {lesions.map((lesion) => {
        const isOpen = openLesions[lesion.id] ?? false
        const data = getScoreData(lesion.id)

        return (
          <div key={lesion.id} className="rounded-md border border-border bg-surface-raised overflow-hidden">
            {/* Header */}
            <button
              onClick={() => toggleLesion(lesion.id)}
              className="flex items-center justify-between w-full px-2.5 py-2 text-[11px] font-medium hover:bg-accent/40 transition-colors"
            >
              <div className="flex items-center gap-2">
                <span
                  className="size-5 rounded flex items-center justify-center text-white font-medium text-[10px]"
                  style={{ backgroundColor: lesion.color }}
                >
                  {lesion.ordinal}
                </span>
                <span className="truncate">{lesion.label}</span>
              </div>
              <ChevronDown className={`size-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} />
            </button>

            {isOpen && (
              <div className="px-2.5 pb-3 space-y-3 border-t border-border pt-2">
                {/* Laterality */}
                <FieldRow label="Laterality">
                  <Select
                    value={data.laterality || null}
                    onValueChange={(v) =>
                      updateField(lesion.id, { laterality: (v ?? "") as PIRADSScore["laterality"] })
                    }
                  >
                    <SelectTrigger size="sm" className="w-full min-w-0">
                      <SelectValue placeholder="Select laterality" />
                    </SelectTrigger>
                    <SelectContent className="w-auto min-w-[var(--anchor-width)] max-w-[90vw]">
                      <SelectItem value="left">Left</SelectItem>
                      <SelectItem value="middle">Middle</SelectItem>
                      <SelectItem value="right">Right</SelectItem>
                    </SelectContent>
                  </Select>
                </FieldRow>

                {/* Level */}
                <FieldRow label="Level">
                  <Select
                    value={data.level || null}
                    onValueChange={(v) => updateField(lesion.id, { level: (v ?? "") as PiradsLevel | "" })}
                  >
                    <SelectTrigger size="sm" className="w-full min-w-0">
                      <SelectValue placeholder="Select level" />
                    </SelectTrigger>
                    <SelectContent className="w-auto min-w-[var(--anchor-width)] max-w-[90vw]">
                      <SelectItem value="apex">Apex</SelectItem>
                      <SelectItem value="mid-apex">Mid-apex</SelectItem>
                      <SelectItem value="mid">Mid</SelectItem>
                      <SelectItem value="base-mid">Base-mid</SelectItem>
                      <SelectItem value="base">Base</SelectItem>
                      <SelectItem value="all levels">All levels</SelectItem>
                    </SelectContent>
                  </Select>
                </FieldRow>

                {/* Zones (up to 4) */}
                <FieldRow label="Zones">
                  <div className="space-y-1">
                    {[0, 1, 2, 3].map((zoneIndex) => {
                      const currentZone = data.zones[zoneIndex] ?? ""
                      return (
                        <Select
                          key={zoneIndex}
                          value={currentZone || null}
                          onValueChange={(v) => {
                            const newZones = updateZonesList(data.zones, zoneIndex, (v ?? "") as PiradsZone | "")
                            // Invalidate reasons when zones change
                            const updates: Partial<PIRADSScore> = { zones: newZones }
                            if (data.T2Score && data.T2Reason && newZones.length > 0) {
                              const valid = getPiradsReasons(newZones[0]!, data.T2Score, "T2", t2Reasons)
                              if (!valid.includes(data.T2Reason)) updates.T2Reason = ""
                            }
                            if (data.DWIScore && data.DWIReason && newZones.length > 0) {
                              const valid = getPiradsReasons(newZones[0]!, data.DWIScore, "DWI", dwiReasons)
                              if (!valid.includes(data.DWIReason)) updates.DWIReason = ""
                            }
                            updateField(lesion.id, updates)
                          }}
                        >
                          <SelectTrigger size="sm" className="w-full min-w-0">
                            <SelectValue placeholder={`Zone ${zoneIndex + 1}`} />
                          </SelectTrigger>
                          <SelectContent className="w-auto min-w-[var(--anchor-width)] max-w-[90vw]">
                            <SelectItem value="AS">AS</SelectItem>
                            <SelectItem value="PZa">PZa</SelectItem>
                            <SelectItem value="PZpl">PZpl</SelectItem>
                            <SelectItem value="PZpm">PZpm</SelectItem>
                            <SelectItem value="TZa">TZa</SelectItem>
                            <SelectItem value="TZp">TZp</SelectItem>
                            <SelectItem value="CZ">CZ</SelectItem>
                          </SelectContent>
                        </Select>
                      )
                    })}
                  </div>
                </FieldRow>

                {/* T2 Score */}
                <ModalitySection
                  label="T2"
                  modality="t2"
                  data={data}
                  onUpdate={(updates) => updateField(lesion.id, updates)}
                  showUpDownGrade
                />

                {/* DWI Score */}
                <ModalitySection
                  label="DWI"
                  modality="dwi"
                  data={data}
                  onUpdate={(updates) => updateField(lesion.id, updates)}
                  showUpDownGrade
                />

                {/* DCE Score */}
                <ModalitySection
                  label="DCE"
                  modality="dce"
                  data={data}
                  onUpdate={(updates) => updateField(lesion.id, updates)}
                  showUpDownGrade={false}
                />

                {/* ISUP Grade (optional pathology correlation) */}
                <FieldRow label="ISUP Grade">
                  <Select
                    value={data.ISUPGrade || null}
                    onValueChange={(v) => updateField(lesion.id, { ISUPGrade: (v ?? "") as ISUPGrade })}
                  >
                    <SelectTrigger size="sm" className="w-full min-w-0">
                      <SelectValue placeholder="Select ISUP grade" />
                    </SelectTrigger>
                    <SelectContent className="w-auto min-w-[var(--anchor-width)] max-w-[90vw]">
                      <SelectItem value="unknown">Unknown</SelectItem>
                      <SelectItem value="no-cancer">No cancer</SelectItem>
                      <SelectItem value="1">1</SelectItem>
                      <SelectItem value="2">2</SelectItem>
                      <SelectItem value="3">3</SelectItem>
                      <SelectItem value="4">4</SelectItem>
                      <SelectItem value="5">5</SelectItem>
                    </SelectContent>
                  </Select>
                </FieldRow>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-1">{label}</div>
      {children}
    </div>
  )
}

interface ModalitySectionProps {
  label: string
  modality: Modality
  data: PIRADSScore
  onUpdate: (updates: Partial<PIRADSScore>) => void
  showUpDownGrade: boolean
}

function ModalitySection({ label, modality, data, onUpdate, showUpDownGrade }: ModalitySectionProps) {
  const scoreFieldKey = SCORE_FIELD_MAP[modality] as keyof PIRADSScore
  const reasonFieldKey = REASON_FIELD_MAP[modality] as keyof PIRADSScore
  const potentialFieldKey = POTENTIAL_FIELD_MAP[modality] as keyof PIRADSScore | ""

  const currentScore = data[scoreFieldKey] as "" | 1 | 2 | 3 | 4 | 5
  const currentReason = data[reasonFieldKey] as string
  const currentPotential = potentialFieldKey ? (data[potentialFieldKey] as string) : ""

  const scoreOptions = getScoreOptions(modality)

  const filteredReasons = useMemo(() => {
    if (!currentScore) return []
    if (modality !== "dce" && data.zones.length === 0) return []
    const reasonData = modality === "t2" ? t2Reasons : modality === "dwi" ? dwiReasons : dceReasons
    const modalityUpperCase = modality.toUpperCase()
    const zoneForReasons = modality === "dce" ? "" : (data.zones[0] ?? "")
    return getPiradsReasons(zoneForReasons, currentScore, modalityUpperCase, reasonData)
  }, [currentScore, data.zones, modality])

  const scoreValue = currentScore === "" ? null : String(currentScore)
  const reasonPlaceholder =
    filteredReasons.length > 0
      ? "Select reason"
      : modality === "dce"
        ? "Select score first"
        : "Select score and zone first"

  return (
    <FieldRow label={`${label} Score`}>
      <div className="space-y-1.5">
        {/* Score + Reason row */}
        <div className="flex gap-1.5">
          <Select
            value={scoreValue}
            onValueChange={(v) => {
              const converted = convertScoreValue(modality, v ?? "")
              const updates: Partial<PIRADSScore> = {
                [scoreFieldKey]: converted,
                [reasonFieldKey]: ""
              }
              if (potentialFieldKey) {
                ;(updates as Record<string, unknown>)[potentialFieldKey] = ""
              }
              onUpdate(updates)
            }}
          >
            <SelectTrigger size="sm" className="w-16 shrink-0">
              <SelectValue placeholder="*" />
            </SelectTrigger>
            <SelectContent className="w-auto min-w-[var(--anchor-width)] max-w-[90vw]">
              {scoreOptions
                .filter((opt) => opt.value !== "")
                .map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>

          <Select
            key={`reason-${String(currentScore)}-${data.zones.join(",")}`}
            value={currentReason || null}
            onValueChange={(v) => onUpdate({ [reasonFieldKey]: v ?? "" } as Partial<PIRADSScore>)}
          >
            <SelectTrigger size="sm" className="flex-1 min-w-0">
              <SelectValue placeholder={reasonPlaceholder} />
            </SelectTrigger>
            <SelectContent className="w-auto min-w-[var(--anchor-width)] max-w-[90vw]">
              {filteredReasons.map((reason) => (
                <SelectItem key={reason} value={reason}>
                  {reason}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Up/Downgrade */}
        {showUpDownGrade && potentialFieldKey && (
          <Select
            key={`potential-${String(currentScore)}`}
            value={currentPotential || null}
            onValueChange={(v) => onUpdate({ [potentialFieldKey]: v ?? "" } as Partial<PIRADSScore>)}
          >
            <SelectTrigger size="sm" className="w-full min-w-0">
              <SelectValue placeholder="Select potential adjustment" />
            </SelectTrigger>
            <SelectContent className="w-auto min-w-[var(--anchor-width)] max-w-[90vw]">
              <SelectItem value="upgrade" disabled={currentScore === 5}>
                Potential upgrade
              </SelectItem>
              <SelectItem value="downgrade" disabled={currentScore === 1}>
                Potential downgrade
              </SelectItem>
              <SelectItem value=" ">No adjustment</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>
    </FieldRow>
  )
}
